import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { FeedPost } from './mock-feed.service';

export interface UserProfile {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  title: string;
  company: string;
  location: string;
  bio: string;
  avatar?: string;
  initials: string;
  followersCount: number;
  followingCount: number;
  postsCount: number;
  resume: Resume;
}

export interface Resume {
  summary: string;
  experience: Experience[];
  education: Education[];
  skills: string[];
  certifications: Certification[];
}

export interface Experience {
  id: string;
  title: string;
  company: string;
  location: string;
  startDate: Date;
  endDate?: Date;
  current: boolean;
  description: string;
}

export interface Education {
  id: string;
  degree: string;
  school: string;
  location: string;
  startDate: Date;
  endDate?: Date;
  current: boolean;
  description?: string;
}

export interface Certification {
  id: string;
  name: string;
  issuer: string;
  issueDate: Date;
  expiryDate?: Date;
  credentialId?: string;
}

export interface Follower {
  id: string;
  name: string;
  title: string;
  company: string;
  avatar?: string;
  initials: string;
  isFollowing: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class MockProfileService {
  private mockProfile: UserProfile = {
    id: 'current-user',
    name: 'Bob Smith',
    firstName: 'Bob',
    lastName: 'Smith',
    email: 'bsmith@dontpaniclabs.com',
    phone: '+1 (555) 123-4567',
    title: 'Senior Software Engineer',
    company: 'TechVantage Solutions',
    location: 'San Francisco, CA',
    bio: 'Passionate software engineer with 10+ years of experience building scalable web applications. Love working with modern technologies and mentoring junior developers.',
    initials: 'BS',
    followersCount: 245,
    followingCount: 189,
    postsCount: 42,
    resume: {
      summary:
        'Experienced software engineer specializing in full-stack development with expertise in Angular, React, Node.js, and cloud technologies. Proven track record of delivering high-quality applications and leading development teams.',
      experience: [
        {
          id: 'exp-1',
          title: 'Senior Software Engineer',
          company: 'TechVantage Solutions',
          location: 'San Francisco, CA',
          startDate: new Date('2020-01-15'),
          current: true,
          description:
            'Lead development of enterprise web applications using Angular and Node.js. Mentor junior developers and collaborate with cross-functional teams to deliver high-quality software solutions.',
        },
        {
          id: 'exp-2',
          title: 'Software Engineer',
          company: 'Digital Dynamics Inc.',
          location: 'Seattle, WA',
          startDate: new Date('2017-06-01'),
          endDate: new Date('2019-12-31'),
          current: false,
          description:
            'Developed and maintained React-based applications. Worked on improving application performance and implementing new features based on user feedback.',
        },
        {
          id: 'exp-3',
          title: 'Junior Developer',
          company: 'CloudSync Technologies',
          location: 'Austin, TX',
          startDate: new Date('2015-03-01'),
          endDate: new Date('2017-05-31'),
          current: false,
          description:
            'Built responsive web interfaces using HTML, CSS, and JavaScript. Collaborated with senior developers to learn best practices and contribute to team projects.',
        },
      ],
      education: [
        {
          id: 'edu-1',
          degree: 'Bachelor of Science in Computer Science',
          school: 'University of California, Berkeley',
          location: 'Berkeley, CA',
          startDate: new Date('2011-09-01'),
          endDate: new Date('2015-05-31'),
          current: false,
          description: 'Graduated Magna Cum Laude. Focused on software engineering and web development.',
        },
      ],
      skills: [
        'Angular',
        'React',
        'TypeScript',
        'JavaScript',
        'Node.js',
        'AWS',
        'Docker',
        'Kubernetes',
        'PostgreSQL',
        'MongoDB',
        'Git',
        'Agile/Scrum',
      ],
      certifications: [
        {
          id: 'cert-1',
          name: 'AWS Certified Solutions Architect',
          issuer: 'Amazon Web Services',
          issueDate: new Date('2022-03-15'),
          expiryDate: new Date('2025-03-15'),
          credentialId: 'AWS-12345',
        },
        {
          id: 'cert-2',
          name: 'Angular Certified Developer',
          issuer: 'Google',
          issueDate: new Date('2021-06-20'),
          credentialId: 'ANG-67890',
        },
      ],
    },
  };

  private mockFollowers: Follower[] = [
    {
      id: 'follower-1',
      name: 'Sarah Johnson',
      title: 'Product Manager',
      company: 'InnovateTech',
      initials: 'SJ',
      isFollowing: true,
    },
    {
      id: 'follower-2',
      name: 'Mike Chen',
      title: 'DevOps Engineer',
      company: 'CloudSync',
      initials: 'MC',
      isFollowing: false,
    },
    {
      id: 'follower-3',
      name: 'Emily Davis',
      title: 'UX Designer',
      company: 'DesignStudio',
      initials: 'ED',
      isFollowing: true,
    },
    {
      id: 'follower-4',
      name: 'David Wilson',
      title: 'Software Architect',
      company: 'TechCorp',
      initials: 'DW',
      isFollowing: true,
    },
    {
      id: 'follower-5',
      name: 'Lisa Anderson',
      title: 'Data Scientist',
      company: 'AnalyticsPro',
      initials: 'LA',
      isFollowing: false,
    },
  ];

  private mockFollowing: Follower[] = [
    {
      id: 'following-1',
      name: 'John Martinez',
      title: 'Engineering Manager',
      company: 'BigTech Inc.',
      initials: 'JM',
      isFollowing: true,
    },
    {
      id: 'following-2',
      name: 'Rachel Green',
      title: 'Full Stack Developer',
      company: 'StartupXYZ',
      initials: 'RG',
      isFollowing: true,
    },
    {
      id: 'following-3',
      name: 'Tom Brown',
      title: 'CTO',
      company: 'TechStart',
      initials: 'TB',
      isFollowing: true,
    },
    {
      id: 'following-4',
      name: 'Amy White',
      title: 'Senior Developer',
      company: 'CodeCraft',
      initials: 'AW',
      isFollowing: true,
    },
  ];

  private mockUserPosts: FeedPost[] = [
    {
      id: 'user-post-1',
      author: {
        id: 'current-user',
        name: 'Bob Smith',
        initials: 'BS',
      },
      date: new Date('2025-01-01T10:00:00'),
      categories: ['IT'],
      content:
        'Just finished an amazing project using Angular 18! The new features make development so much smoother. Excited to share what we built.',
      mediaUrl: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800',
      mediaType: 'image',
      likes: 23,
      comments: 5,
      isLiked: false,
    },
    {
      id: 'user-post-2',
      author: {
        id: 'current-user',
        name: 'Bob Smith',
        initials: 'BS',
      },
      date: new Date('2024-12-30T14:30:00'),
      categories: ['IT', 'Education'],
      content:
        'Gave a talk at the local developer meetup about microservices architecture. Great turnout and engaging questions from the audience!',
      likes: 18,
      comments: 3,
      isLiked: true,
    },
    {
      id: 'user-post-3',
      author: {
        id: 'current-user',
        name: 'Bob Smith',
        initials: 'BS',
      },
      date: new Date('2024-12-28T09:15:00'),
      categories: ['IT'],
      content:
        'Started a new open-source project to help developers manage their AWS infrastructure more efficiently. Check it out on GitHub!',
      likes: 31,
      comments: 8,
      isLiked: false,
    },
  ];

  getProfile(userId?: string): Observable<UserProfile> {
    if (!userId || userId === 'current-user') {
      return of(this.mockProfile).pipe(delay(200));
    }
    
    // Find profile from followers or following
    const follower = this.mockFollowers.find(f => f.id === userId);
    const following = this.mockFollowing.find(f => f.id === userId);
    const person = follower || following;
    
    if (person) {
      // Create a mock profile for this user
      const mockUserProfile: UserProfile = {
        id: person.id,
        name: person.name,
        firstName: person.name.split(' ')[0] || '',
        lastName: person.name.split(' ').slice(1).join(' ') || '',
        email: `${person.name.toLowerCase().replace(' ', '.')}@example.com`,
        title: person.title,
        company: person.company,
        location: 'Location not specified',
        bio: `Professional in ${person.title} at ${person.company}.`,
        initials: person.initials,
        followersCount: Math.floor(Math.random() * 500) + 50,
        followingCount: Math.floor(Math.random() * 300) + 30,
        postsCount: Math.floor(Math.random() * 100) + 10,
        resume: {
          summary: `Experienced ${person.title} with a passion for innovation and excellence.`,
          experience: [],
          education: [],
          skills: [],
          certifications: [],
        },
      };
      return of(mockUserProfile).pipe(delay(200));
    }
    
    // Return current user profile as fallback
    return of(this.mockProfile).pipe(delay(200));
  }

  updateProfile(profile: Partial<UserProfile>): Observable<UserProfile> {
    this.mockProfile = { ...this.mockProfile, ...profile };
    return of(this.mockProfile).pipe(delay(300));
  }

  getFollowers(): Observable<Follower[]> {
    return of(this.mockFollowers).pipe(delay(200));
  }

  getFollowing(): Observable<Follower[]> {
    return of(this.mockFollowing).pipe(delay(200));
  }

  getUserPosts(userId?: string): Observable<FeedPost[]> {
    if (!userId || userId === 'current-user') {
      return of(this.mockUserPosts).pipe(delay(200));
    }
    
    // For other users, return empty posts or filter by userId
    // In a real app, this would fetch posts for that specific user
    return of([]).pipe(delay(200));
  }

  updateResume(resume: Partial<Resume>): Observable<Resume> {
    this.mockProfile.resume = { ...this.mockProfile.resume, ...resume };
    return of(this.mockProfile.resume).pipe(delay(300));
  }
}

